import type { ApiReturnSite } from '@highimpact-frontend/api-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';
import {
  buildMapLayersFromDataSources,
  type MapLayerInfo,
} from '../dashboard/widget/widgets/map/mapSiteGeometries';
import type { Datasource } from '../dashboard/widget/widgets/map/mapTypes';

// -- type definitions --

export interface UseMapSiteLayersResult {
  readonly layers: readonly MapLayerInfo[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly reload: () => Promise<void>;
}

// -- main hook --

/**
 * Loads site geometries for map widgets via the host site catalog API.
 */
export function useMapSiteLayers(
  dataSource: readonly Datasource[]
): UseMapSiteLayersResult {
  const host = useDashboardHost();
  const [sites, setSites] = useState<ApiReturnSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const catalog = await host.api.site.getAll();
      setSites(catalog);
    } catch (loadError) {
      setSites([]);
      setError(
        loadError instanceof Error ? loadError : new Error(String(loadError))
      );
    } finally {
      setLoading(false);
    }
  }, [host.api.site]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const layers = useMemo(
    () => buildMapLayersFromDataSources(dataSource, sites),
    [dataSource, sites]
  );

  return { layers, loading, error, reload };
}
